import WebUploader from "webuploader";
import "webuploader/dist/webuploader.css";

import Evented from '@/utils/Evented.js';
import { Store } from "@/utils/store";
import logServeCof from '@/plugins/logServeCof.js';
import Message from '@/utils/resetMessage';
import { getToken } from "@/utils/generalMethods";

import {
    getFileInfoService,
    publicElemCheckNameService,
    publicElemCreateUploadService,
    publicElemPauseUploadService,
    privateElemCheckNameService,
    privateElemCreateUploadService,
    privateElemPauseUploadService
} from "@/service/interface";

const BLOB_SIZE = 5 * 1024 * 1024;

const AppendInterfaceMap = {
    'CLPublicDoc': 'public',
    'CLPrivateDoc': 'private'
}

const UploadingStatusValue = [0, 1, 2, 6];

class ElemUploader extends Evented {
    constructor(schemeType) {
        super();
        this.schemeType = schemeType;

        // status为前端自定义状态，状态说明如下：
        // waiting：某个文件等待上传
        // continue：某个文件断点续传
        this.status = "waiting";

        this.uploadFiles = [];          // 等待上传的文件
        this.uploadingFiles = [];       // 上传中的文件
        this.pausedFiles = [];          // 上传暂停的文件

        this.currNodeId = "";
        this.uploadPathName = "";
        this.continueFlag = false;
        this.currFileInfo = null;       // 设置文件信息，用于断点续传和文件更新
        this.fileUploadMode = "";       // 文件上传模式，调用append接口时需要在header里面标识

        this.onFilesQueued = this.onFilesQueued.bind(this);
        this.onUploadBeforeSend = this.onUploadBeforeSend.bind(this);
        this.onUploadAccept = this.onUploadAccept.bind(this);
        this.onUploadSuccess = this.onUploadSuccess.bind(this);
        this.onUploadError = this.onUploadError.bind(this);

        this.onPauseFileUpload = this.onPauseFileUpload.bind(this);
        this.onContinueFileUpload = this.onContinueFileUpload.bind(this);

        this.initUploader();
        this.addUploaderEventsListener();

        this.addEvnetListener();
    }

    initUploader() {
        const { uploaders } = Store;
        const find = uploaders.find(el => el.schemeType === this.schemeType);
        if (!find) {
            this.uploader = this.createUploader();
            uploaders.push({ schemeType: this.schemeType, uploader: this });
        }
    }

    createUploader() {
        let webUploader = WebUploader.create({
            // swf文件路径
            swf: "../../node_modules/webuploader/dist/Uploader.swf",

            // 文件接收服务端。
            server: `${window.serverUrl}/${logServeCof.logServeCof()}/project/componentLibrary/v3/${AppendInterfaceMap[this.schemeType]}/append`,
            timeout: window.uploadTimeout,

            accept: {
                title: "构件上传",
                extensions: "*",
                mimeTypes: ".gltf,.glb,.rfa,.fbx,.obj,.obj.zip",
            },
            chunked: true, // 分片处理大文件
            chunkSize: BLOB_SIZE,
            fileVal: "FileInfo",
            threads: 1,
            chunkRetry: 0,
            duplicate: true,

            auto: false,
        });
        return webUploader;
    }

    addUploaderEventsListener() {
        this.uploader.on("filesQueued", this.onFilesQueued);
        this.uploader.on("uploadBeforeSend", this.onUploadBeforeSend);
        this.uploader.on("uploadAccept", this.onUploadAccept);
        this.uploader.on("uploadSuccess", this.onUploadSuccess);
        this.uploader.on("uploadError", this.onUploadError);
    }

    addEvnetListener() {
        this.on('pauseFileUpload', this.onPauseFileUpload);
        this.on('continueFileUpload', this.onContinueFileUpload);
    }

    onFilesQueued(files) {
        let filesInUploadQueue = [];
        let invalidFileNamefiles = [];      // 文件名不合法的文件

        files.forEach(file => {
            file.fileName = file.name;
            file.length = file.size;
            file.schemeType = this.schemeType;

            let fileExtensName = file.name.substring(file.name.lastIndexOf(".") + 1);
            let fileInfo = {
                id: WebUploader.Base.guid(),
                nodeId: this.currNodeId,
                levelPath: this.uploadPathName,
                schemeType: this.schemeType,
                // 文件状态，前端自定义(waiting: 等待上传, created: 已创建上传索引, paused: 已暂停, continue: 断点续传, update: 文件更新)
                customStatus: this.status,

                // 是否已经过重名校验
                isChecked: this.isChecked(),

                // 以下为上传列表中需要展示的内容
                fileName: file.name,
                fileType: fileExtensName,
                length: file.size,
                creationTime: new Date().toUTCString(),
                rate: 0,
                status: this.getFileStatus(),

                // 文件对象
                file,
            }

            if (this.status === 'continue') {
                if (this.currFileInfo.fileName !== fileInfo.fileName) {
                    Message.warning("文件名不匹配，请重试");
                    return;
                }
                fileInfo["fileId"] = this.currFileInfo.fileId;
                fileInfo["nodeId"] = this.currFileInfo.nodeId;
                fileInfo["fileDataId"] = this.currFileInfo.fileDataId;
            }

            let isFileInUploadQueue = this.isFileInUploadQueue(fileInfo);
            let isInvalidFileName = this.isInvalidFileName(fileInfo);
            if (isFileInUploadQueue) {
                filesInUploadQueue.push(fileInfo);
            } else if (isInvalidFileName) {
                invalidFileNamefiles.push(fileInfo);
            } else {
                this.uploadFiles.push(fileInfo);
            }
        })

        if (filesInUploadQueue.length) {
            let fileNameList = filesInUploadQueue.map(el => el.fileName);
            let message = this.getWarningMessage(fileNameList, '已在上传队列')
            Message.error(message);
        }

        if (invalidFileNamefiles.length) {
            let fileNameList = invalidFileNamefiles.map(el => el.fileName);
            let message = this.getWarningMessage(fileNameList, '名称存在无效的字符(开头与结尾不能为空，不能包含\\字段，文件名称后缀前不允许有空格)')
            Message.error(message);
        }

        this.handleUploadFiles();
    }

    onUploadBeforeSend(object, data, headers) {
        headers["Authorization"] = getToken();

        data["Index"] = data.chunk ? data.chunk : 0;

        if (this.continueFlag) {
            this.setAppendUrl('append');
            this.continueFlag = false;
        }
    }


    onUploadAccept() {
        let data = this.uploader.options.formData;
        if (data.pauseFlag) {
            // webuploader的stop方法有两种模式
            // 1、暂停自动上传下个文件，但是当前文件会继续传完。
            // 2、暂停自动上传下个文件，且中断当前文件上传。(如果是分块上传，只会中断当前分块)
            // 此处需要暂停当前文件的继续上传，因此需要传入true
            this.uploader.stop(true);
            this.pauseFileUpload(data.pauseInfo);
        }
    }

    onUploadSuccess(file) {
        let findIndex = this.uploadingFiles.findIndex((el) => this.isSameFile(el, file));
        if (findIndex !== -1) {
            this.uploadingFiles.splice(findIndex, 1);
            this.uploader.removeFile(file, true);
            this.uploader.stop();
            this.status === "waiting";

            if (this.uploadFiles.length) {
                let uploadingFile = this.uploadFiles.shift();
                this.uploadingFiles.push(uploadingFile);
                this.handleUploadSuccess(uploadingFile);
            }

            this.fire('uploadFinish', {});
        }
    }

    onUploadError(file) {
        let findIndex = this.uploadingFiles.findIndex((el) => this.isSameFile(el, file));
        if (findIndex !== -1) {
            this.uploadingFiles.splice(findIndex, 1);
            this.uploader.removeFile(file, true);
            this.uploader.stop();
            this.status === "waiting";

            if (this.uploadFiles.length) {
                let uploadingFile = this.uploadFiles.shift();
                this.uploadingFiles.push(uploadingFile);
                this.handleUploadSuccess(uploadingFile);
            }

            this.fire('uploadFinish', {});
        }
    }

    handleUploadFiles() {
        let filesToCheck = this.uploadFiles.filter(el => !el.isChecked);
        this.sameFileNameCheck(filesToCheck).then(checkResult => {
            let convertingFiles = checkResult.convertingFiles;
            if (convertingFiles.length) {
                let fileNameList = convertingFiles.map(el => el.fileName);
                let message = this.getWarningMessage(fileNameList, '正在转换或等待转换，暂时不能上传')
                Message.error(message);

                this.removeConvertingFilesFromUploadFiles(convertingFiles);
            }

            let repeatedFiles = checkResult.repeatedFiles;
            if (repeatedFiles.length) {
                this.removeUploadingFilesFromUploadFiles(repeatedFiles);
            }

            this.handleRepeatedFiles(repeatedFiles).then(() => {
                if (this.uploadFiles.length) {
                    this.startUploadFile();
                }
            })
        })
    }

    handleRepeatedFiles(repeatedFiles) {
        return new Promise(resolve => {
            if (!repeatedFiles.length) {
                resolve();
                return;
            }

            let fileNameList = repeatedFiles.map(el => el.fileName);
            let message = this.getWarningMessage(fileNameList, '已存在');
            Message.error(message);

            repeatedFiles.forEach(fileInfo => {
                let findIndex = this.uploadFiles.findIndex(el => el.fileName === fileInfo.fileName);
                if (findIndex !== -1) {
                    this.uploadFiles.splice(findIndex, 1);
                }
            })
            resolve();
        })
    }

    startUploadFile() {
        this.fire('startUploadFile', {});

        if (this.uploadingFiles.length) {
            Message.warning("当前有文件正在上传，请耐心等待");
            return;
        }

        let uploadingFile = this.uploadFiles.shift();

        if (uploadingFile.customStatus === 'waiting') {
            this.uploadingFiles.push(uploadingFile);

            this.createFileUpLoad(uploadingFile).then(() => {
                this.uploader.upload();
            });
        }

        if (uploadingFile.customStatus === 'continue') {
            this.uploadingFiles.push(uploadingFile);

            this.getFileInfo(uploadingFile).then(() => {
                this.continueFlag = true;
                this.setAppendUrl('reAppend');

                this.uploader.upload();
            });
        }
    }

    handleUploadSuccess(uploadingFile) {
        if (uploadingFile.customStatus === 'waiting') {
            this.createFileUpLoad(uploadingFile).then(() => {
                // 延时执行以触发文件上传事件
                setTimeout(() => {
                    this.uploader.upload();
                }, 500);

            });
        }
        if (uploadingFile.customStatus === 'continue') {
            this.getFileInfo(uploadingFile).then(() => {
                this.fileUploadMode = 'ResumableReAppend';
                // 延时执行以触发文件上传事件
                setTimeout(() => {
                    this.continueFlag = true;
                    this.setAppendUrl('reAppend');

                    this.uploader.upload();
                }, 500);
            });
        }
    }

    createFileUpLoad(fileInfo) {
        return new Promise((resolve) => {
            let count = fileInfo.length / (BLOB_SIZE);

            let params = {
                fileName: fileInfo.fileName,
                fileSize: fileInfo.length,
                blobCount: Math.ceil(count),
                blobSize: BLOB_SIZE,
                nodeId: fileInfo.nodeId
            };

            let headers = {
                Authorization: getToken()
            }

            switch (this.schemeType) {
                case 'CLPublicDoc': {
                    publicElemCreateUploadService(params, { headers }).then((response) => {
                        if (response.isSuccess) {
                            let result = response.data;
                            if (!result.fileUploaderOverflow) {
                                this.uploader.options.formData["FileId"] = result.fileId;
                                this.uploader.options.formData["FileDataId"] = result.fileDataId;
                                this.uploader.options.formData["Key"] = result.fileKey;

                                fileInfo["fileId"] = result.fileId;
                                fileInfo["fileDataId"] = result.fileDataId;
                                fileInfo["Key"] = result.fileKey;

                                resolve(result);
                            } else {
                                this.handleFileOverflow(fileInfo);
                            }
                        } else {
                            Message.error(response.errMsg);
                            this.handleFileCreateFail(fileInfo);
                        }
                    });
                    break;
                }
                case 'CLPrivateDoc': {
                    privateElemCreateUploadService(params, { headers }).then((response) => {
                        if (response.isSuccess) {
                            let result = response.data;
                            if (!result.fileUploaderOverflow) {
                                this.uploader.options.formData["FileId"] = result.fileId;
                                this.uploader.options.formData["FileDataId"] = result.fileDataId;
                                this.uploader.options.formData["Key"] = result.fileKey;

                                fileInfo["fileId"] = result.fileId;
                                fileInfo["fileDataId"] = result.fileDataId;
                                fileInfo["Key"] = result.fileKey;

                                resolve(result);
                            } else {
                                this.handleFileOverflow(fileInfo);
                            }
                        } else {
                            Message.error(response.errMsg);
                            this.handleFileCreateFail(fileInfo);
                        }
                    });
                    break;
                }
            }
        })
    }

    onPauseFileUpload(eventInfo) {
        let { fileInfo } = eventInfo;

        let find = this.uploadingFiles.find(el => el.fileDataId === fileInfo.fileDataId);
        if (find) {
            this.uploader.options.formData.pauseFlag = true;
            this.uploader.options.formData.pauseInfo = {
                fileId: fileInfo.fileId,
                fileDataId: fileInfo.fileDataId,
                key: fileInfo.key,
                fileName: fileInfo.fileName,
                length: fileInfo.length,
            }
        } else {
            this.pauseFileUpload(fileInfo);
        }
    }

    pauseFileUpload(pauseInfo) {
        let params = {
            fileId: pauseInfo.fileId,
            fileDataId: pauseInfo.fileDataId,
            fileKey: pauseInfo.key
        }

        switch (this.schemeType) {
            case 'CLPublicDoc': {
                publicElemPauseUploadService(params).then((response) => {
                    if (response.isSuccess) {
                        this.handleFilePauseSuccess(pauseInfo);
                    }
                });
                break;
            }
            case 'CLPrivateDoc': {
                privateElemPauseUploadService(params).then((response) => {
                    if (response.isSuccess) {
                        this.handleFilePauseSuccess(pauseInfo);
                    }
                });
                break;
            }
        }
    }

    onContinueFileUpload(eventInfo) {
        let { fileInfo } = eventInfo;
        let findIndex = this.pausedFiles.findIndex((el) => el.fileDataId === fileInfo.fileDataId);
        if (findIndex !== -1) {
            let uploadingFile = this.pausedFiles[findIndex];
            this.pausedFiles.splice(findIndex, 1);

            this.continueFlag = true;
            this.setAppendUrl('reAppend');

            this.uploader.addFiles(uploadingFile.file);
        }
    }

    sameFileNameCheck(filesToCheck) {
        return new Promise(resolve => {
            if (!filesToCheck.length) {
                resolve({ repeatedFiles: [], convertingFiles: [] });
                return;
            }

            filesToCheck.forEach(el => el.isChecked = true);

            let fileName = filesToCheck.map(el => el.fileName)
            let params = { parentId: this.currNodeId, fileName };
            switch (this.schemeType) {
                case 'CLPublicDoc': {
                    publicElemCheckNameService(params).then(response => {
                        if (response) {
                            let data = response.data;
                            let repeatedFiles = data.repeateds;
                            let convertingFiles = data.specialStatus;
                            resolve({ repeatedFiles, convertingFiles });
                        }
                    })
                    break;
                }
                case 'CLPrivateDoc': {
                    privateElemCheckNameService(params).then(response => {
                        if (response) {
                            let data = response.data;
                            let repeatedFiles = data.repeateds;
                            let convertingFiles = data.specialStatus;
                            resolve({ repeatedFiles, convertingFiles });
                        }
                    })
                    break;
                }
            }
        })
    }

    /**
     * 处理超出文件同时上传个数的情况
     * @param {Object} fileInfo 
     */
    handleFileOverflow(fileInfo) {
        Message.error('超出同时上传文件个数！！！');
        let findIndex = this.uploadingFiles.findIndex(el => this.isSameFile(el, fileInfo))
        if (findIndex !== -1) {
            this.uploadingFiles.splice(findIndex, 1)
            this.uploader.removeFile(fileInfo.file, true);
        }

        if (this.uploadFiles.length) {
            let uploadingFile = this.uploadFiles.shift();
            this.uploadingFiles.push(uploadingFile);
            this.handleUploadSuccess(uploadingFile);
        }

        this.fire('uploadFinish', {});
    }

    /**
    * 处理创建文件索引失败
    * @param {Object} fileInfo 
    */
    handleFileCreateFail(fileInfo) {
        let findIndex = this.uploadingFiles.findIndex(el => this.isSameFile(el, fileInfo))
        if (findIndex !== -1) {
            this.uploadingFiles.splice(findIndex, 1)
            this.uploader.removeFile(fileInfo.file, true);
        }

        if (this.uploadFiles.length) {
            let uploadingFile = this.uploadFiles.shift();
            this.uploadingFiles.push(uploadingFile);
            this.handleUploadSuccess(uploadingFile);
        }

        this.fire('uploadFinish', {});
    }


    /**
     * 处理文件暂停成功
     * @param {Object} pauseInfo 
     */
    handleFilePauseSuccess(pauseInfo) {
        Message.success("文件暂停成功");
        delete this.uploader.options.formData.pauseFlag;
        delete this.uploader.options.formData.pauseInfo;
        let findIndex = this.uploadingFiles.findIndex((el) => this.isSameFile(el, pauseInfo));
        if (findIndex !== -1) {
            let fileInfo = this.uploadingFiles[findIndex];
            this.uploadingFiles.splice(findIndex, 1);
            this.uploader.removeFile(fileInfo.file, true);

            fileInfo.customStatus = 'paused';
            fileInfo.status = 2;
            this.pausedFiles.push(fileInfo);
            if (this.uploadFiles.length) {
                let uploadingFile = this.uploadFiles.shift();
                this.uploadingFiles.push(uploadingFile);
                this.handleUploadSuccess(uploadingFile);
            }

            this.fire('uploadFinish', {});
        }
    }

    /**
   * 获取已上传文件信息，用于分片上传判断上传到哪一片
   * @param {Object} fileInfo 
   */
    getFileInfo(fileInfo) {
        return new Promise(resolve => {
            getFileInfoService(fileInfo.fileDataId).then((response) => {
                if (response.isSuccess) {
                    let data = response.data;

                    this.uploader.options.formData['FileId'] = fileInfo.fileId;
                    this.uploader.options.formData["FileDataId"] = fileInfo.fileDataId;
                    this.uploader.options.formData["Key"] = data.key;

                    fileInfo["blobIndex"] = data.blobIndex;
                    resolve();
                }
            })
        })
    }

    removeConvertingFilesFromUploadFiles(convertingFiles) {
        convertingFiles.forEach(fileInfo => {
            let findConvertingFileIndex = this.uploadFiles.findIndex(el => el.fileName === fileInfo.fileName);
            if (findConvertingFileIndex !== -1) {
                this.uploadFiles.splice(findConvertingFileIndex, 1);
            }
        })
    }

    removeUploadingFilesFromUploadFiles(repeatedFiles) {
        let uploadingFiles = repeatedFiles.filter(el => UploadingStatusValue.includes(el.status));
        uploadingFiles.forEach(fileInfo => {
            let findIndex = this.uploadFiles.findIndex(el => el.fileName === fileInfo.fileName)
            if (findIndex !== -1) {
                this.uploadFiles.splice(findIndex, 1);
            }

            let findIndexFromRepeatedFiles = repeatedFiles.findIndex(el => el.fileId === fileInfo.fileId);
            if (findIndexFromRepeatedFiles !== -1) {
                repeatedFiles.splice(findIndexFromRepeatedFiles, 1)
            }
        })
    }

    isFileInUploadQueue(fileInfo) {
        let fileInUploadFiles = this.uploadFiles.find(el => this.isSameFile(el, fileInfo) && el.nodeId === fileInfo.nodeId);
        let fileInUploadingFiles = this.uploadingFiles.find(el => this.isSameFile(el, fileInfo) && el.nodeId === fileInfo.nodeId);
        return fileInUploadFiles || fileInUploadingFiles;
    }

    isInvalidFileName(fileInfo) {
        let fileName = fileInfo.fileName.substring(0, fileInfo.fileName.lastIndexOf("."));
        if (fileName.length !== fileName.trimStart().trimEnd().length || fileName.includes('\\')) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * 文件是否经过同名校验(断点续传和重新上传的文件不需要重名校验)
     * @returns {Boolean} 是否校验过
     */
    isChecked() {
        if (this.status === 'waiting') {
            return false;
        } else {
            return true;
        }
    }

    getFileStatus() {
        let status = null;
        switch (this.status) {
            case 'waiting':
                status = -1;
                break;
            case 'continue':
                status = -2;
                break;

        }
        return status;
    }

    isSameFile(fileInfo1, fileInfo2) {
        return fileInfo1.fileName === fileInfo2.fileName && fileInfo1.length === fileInfo2.length
    }

    setStatus(status) {
        this.status = status;
    }

    setCurrNodeId(currNodeId) {
        this.currNodeId = currNodeId;
    }

    setUploadPathName(uploadPathName) {
        this.uploadPathName = uploadPathName;
    }

    setCurrFileInfo(fileInfo) {
        this.currFileInfo = fileInfo;
    }

    getWarningMessage(fileNameList, messageTail) {
        let message = "";
        let messageHead = "";
        // 超过两个文件只展示前两个
        if (fileNameList.length > 2) {
            messageHead = `${fileNameList[0]}、${fileNameList[1]}等${fileNameList.length}个文件`;
        } else {
            messageHead = fileNameList.join('、');
        }
        message = `${messageHead}${messageTail}`
        return message;
    }

    setAppendUrl(appendType) {
        this.uploader.options.server = `${window.serverUrl}/${logServeCof.logServeCof()}/project/componentLibrary/v3/${AppendInterfaceMap[this.schemeType]}/${appendType}`;
    }
}

export default ElemUploader;